<template>
    <div style="height: 145px">
        <v-row no-gutters style="height: 45px; max-width: 275px">
            <div data-cy="custom-multi-selector">
                <custom-multi-select :value="additionalInfoSelected" :items="additionalInfoItems" item-text="label" item-value="key" :menu-props="{ maxHeight: '330' }" prepend-icon="mdi-bulletin-board" :label="$t('orders.additionalInfo.title')" @selection-change="selectionChange">
                    <template v-slot:selection="{ index }">
                        <span v-if="countOptionsSelected == 1">&nbsp;({{ $t('orders.additionalInfo.oneSelected') }})</span>
                        <span v-else-if="index == 0"> &nbsp;({{ $t('orders.additionalInfo.manySelected', { count: countOptionsSelected }) }}) </span>
                    </template>
                </custom-multi-select>
            </div>

            <div>
                <!-- Width of div (and content) to be defined when this is used -->
                <portal-target name="options-right" class="additional-info-list" />
            </div>
        </v-row>

        <portal-target name="options-bottom" class="additional-info-list" style="margin-top: 10px" />

        <portal :to="checkOptionsPosition">
            <v-row no-gutters class="order-additional-info-row">
                <v-col v-if="ckBudgetRequest" cols="12" md="12" sm="12" class="px-2 py-0 ck-field-name">
                    <v-checkbox v-model="ckBudgetRequest" :label="$t('orders.additionalInfo.budgetRequest')" hide-details style="max-width: 275px" data-cy="additional-info-budget"></v-checkbox>
                </v-col>

                <v-col v-if="ckScheduleOrder" cols="12" md="7" sm="7" class="px-2 py-0 ck-field-name">
                    <v-checkbox v-model="ckScheduleOrder" :label="$t('orders.additionalInfo.scheduleOrder')" hide-details style="max-width: 275px" data-cy="additional-info-schedule"></v-checkbox>
                </v-col>

                <v-col v-if="ckScheduleOrder" cols="12" md="4" sm="4" class="px-2 py-0 large-side-field">
                    <date-picker-menu v-if="ckScheduleOrder" v-model="scheduleOrderDate" :disabled="!ckScheduleOrder" hide-details :inOrderDetails="true" :centered="false" :isfutureDate="true"> </date-picker-menu>
                </v-col>
            </v-row>

            <v-row v-if="ckLaboratoryOrder" no-gutters class="order-additional-info-row">
                <v-col cols="12" md="12" sm="12" class="px-2 py-0 ck-field-name">
                    <v-checkbox v-model="ckLaboratoryOrder" :label="$t('orders.additionalInfo.laboratoryOrder')" hide-details style="width: 275px" data-cy="additional-info-laboratory"></v-checkbox>
                </v-col>
            </v-row>

            <v-row v-if="ckFirstOrder" no-gutters class="order-additional-info-row">
                <v-col cols="12" md="7" sm="7" class="px-2 py-0 ck-field-name">
                    <v-checkbox v-model="ckFirstOrder" :label="$t('orders.additionalInfo.firstOrder')" hide-details style="width: 275px" data-cy="additional-info-first-order"></v-checkbox>
                </v-col>

                <v-col cols="12" md="4" sm="4" class="px-2 py-0 large-side-field">
                    <v-select :label="$t('orders.additionalInfo.discount')" v-model="firstOrderDiscount" :items="firstOrderDiscounts" item-value="id" return-object dense hide-details class="pt-2" data-cy="discount-select">
                        <template v-slot:item="{ item }">
                            <span data-cy="discount-select-item">{{ item.percentage }} %</span>
                        </template>
                        <template v-slot:selection="{ item }">
                            <span class="input-selection" data-cy="discount-select-value">{{ item.percentage }} %</span>
                        </template>
                    </v-select>
                </v-col>
            </v-row>
        </portal>
    </div>
</template>

<script>
import { mapStores } from 'pinia';
import { useUserStore } from '@/Stores/user';
import { useBranchStore } from '@/Stores/branch';
import { useNormalOrderStore } from '@/Stores/normalorder';
import { useAffinityOrderStore } from '@/Stores/affinityorder';

export default {
    props: {
        optionsPosition: { type: String, default: 'bottom' },
        firstOrderDiscounts: Array,
        isAffinity: Boolean,
    },

    data: () => ({
        additionalInfo: [
            { labelKey: 'orders.additionalInfo.budgetRequest', key: 0 },
            { labelKey: 'orders.additionalInfo.scheduleOrder', key: 1 },
            { labelKey: 'orders.additionalInfo.laboratoryOrder', key: 2 },
            { labelKey: 'orders.additionalInfo.firstOrder', key: 3 },
        ],
    }),

    mounted() {
        this.toggleInitialOptions();
    },

    watch: {
        'branchStore.branch': {
            handler: function (curr, prev) {
                // 'prev' being null (initial setup on page load) is indirectly taken into accout on mounted()'s initial toggling
                if (curr?.id !== prev?.id) {
                    this.toggleActiveOptions();
                }
            },
        },
    },

    computed: {
        additionalInfoItems() {
            return this.additionalInfo.map((info) => ({ ...info, label: this.$t(info.labelKey) }));
        },

        ...mapStores(useUserStore, useBranchStore, useNormalOrderStore, useAffinityOrderStore),

        checkOptionsPosition() {
            return this.optionsPosition == 'bottom' ? 'options-bottom' : 'options-right';
        },

        ckBudgetRequest: {
            get() {
                if (this.isAffinity) {
                    return this.affinityOrderStore.isBudgetRequest;
                } else {
                    return this.normalOrderStore.isBudgetRequest;
                }
            },
            set(value) {
                if (this.isAffinity) {
                    this.affinityOrderStore.setIsBudgetRequest(value, this.showWarning);
                } else {
                    this.normalOrderStore.setIsBudgetRequest({ isRequest: value, sendSharedCart: this.normalOrderStore.getEnableSharedCartTriggers });
                }
            },
        },

        ckScheduleOrder: {
            get() {
                if (this.isAffinity) {
                    return this.affinityOrderStore.isScheduledOrder;
                } else {
                    return this.normalOrderStore.isScheduledOrder;
                }
            },
            set(value) {
                if (this.isAffinity) {
                    this.affinityOrderStore.setIsScheduledOrder(value);
                } else {
                    this.normalOrderStore.setIsScheduledOrder({ isRequest: value, sendSharedCart: this.normalOrderStore.getEnableSharedCartTriggers });
                }
            },
        },

        scheduleOrderDate: {
            get() {
                if (this.isAffinity) {
                    return this.affinityOrderStore.getScheduledOrderDate;
                } else {
                    return this.normalOrderStore.getScheduledOrderDate;
                }
            },
            set(value) {
                if (this.isAffinity) {
                    this.affinityOrderStore.setScheduledOrderDate(value);
                } else {
                    this.normalOrderStore.setScheduledOrderDate({ newDate: value, sendSharedCart: this.normalOrderStore.getEnableSharedCartTriggers });
                }
            },
        },

        ckFirstOrder: {
            get() {
                if (this.isAffinity) {
                    return this.affinityOrderStore.isFirstOrder;
                } else {
                    return this.normalOrderStore.isFirstOrder;
                }
            },
            set(value) {
                if (this.isAffinity) {
                    this.affinityOrderStore.setIsFirstOrder(value);
                } else {
                    this.normalOrderStore.setIsFirstOrder({ isRequest: value, sendSharedCart: this.normalOrderStore.getEnableSharedCartTriggers });
                }
            },
        },

        ckLaboratoryOrder: {
            get() {
                if (this.isAffinity) {
                    return this.affinityOrderStore.isLaboratoryOrder;
                } else {
                    return this.normalOrderStore.isLaboratoryOrder;
                }
            },
            set(value) {
                if (this.isAffinity) {
                    this.affinityOrderStore.setIsLaboratoryOrder(value);
                } else {
                    this.normalOrderStore.setIsLaboratoryOrder({ isRequest: value, sendSharedCart: this.normalOrderStore.getEnableSharedCartTriggers });
                }
            },
        },

        firstOrderDiscount: {
            get() {
                if (this.isAffinity) {
                    return this.affinityOrderStore.getFirstOrderDiscount;
                } else {
                    return this.normalOrderStore.getFirstOrderDiscount;
                }
            },
            set(value) {
                if (this.isAffinity) {
                    this.affinityOrderStore.setFirstOrderDiscount(value);
                } else {
                    this.normalOrderStore.setFirstOrderDiscount({ discount: value, sendSharedCart: this.normalOrderStore.getEnableSharedCartTriggers });
                }
            },
        },

        additionalInfoSelected: {
            get() {
                if (this.isAffinity) {
                    return this.affinityOrderStore.selectedAdditionalInfo;
                } else {
                    return this.normalOrderStore.selectedAdditionalInfo;
                }
            },
            set(value) {
                if (this.isAffinity) {
                    this.affinityOrderStore.setSelectedAdditionalInfo(value);
                } else {
                    this.normalOrderStore.setSelectedAdditionalInfo({ options: value, sendSharedCart: this.normalOrderStore.getEnableSharedCartTriggers });
                }
            },
        },

        countOptionsSelected() {
            return this.additionalInfoSelected.length;
        },
    },

    methods: {
        selectionChange(newArray) {
            this.additionalInfoSelected = newArray;
            this.toggleActiveOptions();
        },

        toggleActiveOptions(pageInit = false) {
            // If on pageInit, disable sending data while doing the initial page setup
            // We also restore this value at the end of this function
            if (pageInit) {
                this.normalOrderStore.setEnableSharedCartTriggers(false);
            }

            //{ label: "Programar Encomenda", key: 1 },
            if (this.additionalInfoSelected?.find((option) => option.key == 1) !== undefined) {
                this.ckScheduleOrder = true;
            } else {
                this.ckScheduleOrder = false;
            }

            //{ label: "Encomenda Laboratório", key: 2 },
            if (this.additionalInfoSelected?.find((option) => option.key == 2) !== undefined) {
                this.ckLaboratoryOrder = true;
            } else {
                this.ckLaboratoryOrder = false;
            }

            //{ label: "Primeira Encomenda", key: 3 }
            if (this.additionalInfoSelected?.find((option) => option.key == 3) !== undefined) {
                this.ckFirstOrder = true;
            } else {
                this.ckFirstOrder = false;
            }

            //{ label: "Pedido de Orçamento", key: 0 }
            // Checked last to show a warning correctly when choosing another field while this is already selected
            if (this.additionalInfoSelected?.find((option) => option.key == 0) !== undefined && this.additionalInfoSelected?.find((option) => option.key == 0) !== -1) {
                this.ckBudgetRequest = true;

                let indexToClear = this.additionalInfoSelected?.findIndex((option) => option.key == 1);
                if (indexToClear != -1) {
                    this.additionalInfoSelected?.splice(indexToClear, 1);
                }
                indexToClear = null;

                indexToClear = this.additionalInfoSelected?.findIndex((option) => option.key == 2);
                if (indexToClear != -1) {
                    this.additionalInfoSelected?.splice(indexToClear, 1);
                }
                indexToClear = null;

                indexToClear = this.additionalInfoSelected?.findIndex((option) => option.key == 3);
                if (indexToClear != -1) {
                    this.additionalInfoSelected?.splice(indexToClear, 1);
                }
            } else {
                this.ckBudgetRequest = false;
            }

            if (pageInit) {
                this.normalOrderStore.setEnableSharedCartTriggers(true);
            }
        },

        toggleInitialOptions() {
            if (!this.additionalInfoSelected) {
                this.additionalInfoSelected = [];
            }

            this.toggleActiveOptions(true);
        },
    },
};
</script>

<style>
div.v-textarea .v-text-field__slot label.v-label {
    font-size: 0.75rem !important;
    line-height: 25px !important;
    color: #154579 !important;
}

.additional-info-list .v-input--checkbox .v-input__slot label.v-label {
    font-size: 0.75rem !important;
    font-weight: 600;
    line-height: 25px !important;
    color: #154579;
}

.ck-field-name {
    max-width: 295px;
    margin-top: -7px;
}
</style>

<style scoped>
.large-side-field {
    max-width: 130px;
}

.input-selection {
    font-size: 0.75rem !important;
    line-height: 25px !important;
    color: #154579 !important;
    text-align: left !important;
}
</style>
